// Tour content - all tooltip titles and descriptions in one place
export const tourContent = {
  // Project Selection Page
  projectSelection: {

    projectInput: {
      title: 'Project Path Input - Manual Entry',
      content: 'Enter the full path to your project directory. Type directly or use the Browse button for visual navigation.',
      label: 'BEST FOR',
      labelText: 'Manual path input, autocomplete support, project directory management',
      tip: 'Use Tab for path completion or Browse button for easier navigation'
    },
    browseButton: {
      title: 'Browse Projects - Visual Navigation',
      content: 'Navigate to your project folder using the system file browser. The most intuitive way to locate and select your project directory.',
      label: 'BEST FOR',
      labelText: 'Visual folder selection, file system navigation, project discovery',
      tip: 'Perfect for users who prefer visual navigation over typing paths'
    },
    cloneButton: {
      title: 'Clone Repository - Import Projects',
      content: 'Import projects directly from Git repositories. Simply provide the repository URL and Kolega handles the complete setup process.',
      label: 'BEST FOR',
      labelText: 'GitHub projects, GitLab repositories, existing codebases, team collaboration',
      tip: 'Ideal for starting with existing codebases or collaborating on shared projects'
    },
    refreshButton: {
      title: 'Refresh Projects - Update List',
      content: 'Update your recent projects list to reflect the latest changes and newly created projects.',
      label: 'BEST FOR',
      labelText: 'Syncing project status, updating recent projects, refreshing workspace',
      tip: 'Use this if you created projects outside of Kolega or need to update project status'
    },
    recentProjects: {
      title: 'Recent Projects - Quick Access',
      content: 'Your recently accessed projects for quick reopening. Projects are automatically tracked and organized by last access time.',
      label: 'BEST FOR',
      labelText: 'Quick project access, recent project management, workspace navigation',
      tip: 'Click any project to quickly reopen and continue where you left off'
    },
    deletedProjects: {
      title: 'Deleted Projects - Recovery Center',
      content: 'Access and restore projects that were previously removed. Your safety net for accidental deletions.',
      label: 'BEST FOR',
      labelText: 'Project restoration, recovery operations, permanent deletion management',
      tip: 'Projects remain recoverable until permanently deleted from this section'
    },
    createProject: {
      title: 'Create New Project - Start Building',
      content: 'Launch the project creation wizard to build something amazing. Choose from templates or start completely fresh.',
      label: 'BEST FOR',
      labelText: 'New projects, template selection, custom development, creative initiatives',
      tip: 'Your gateway to bringing new ideas to life with professional templates'
    }
  },

  // New Project Page
  newProject: {
    pageTitle: {
      title: 'Project Creation Hub - Template Selection',
      content: 'Design your next breakthrough project with professional templates and custom configurations tailored to your vision.',
      label: 'BEST FOR',
      labelText: 'Template selection, project configuration, development initialization',
      tip: 'Take your time choosing - the right foundation accelerates your entire development process'
    },
    projectName: {
      title: 'Project Identity - Workspace Naming',
      content: 'Define your project name - this becomes your workspace identifier and folder name. Choose something memorable and descriptive.',
      label: 'BEST FOR',
      labelText: 'Workspace identification, folder naming, project organization',
      tip: 'Use descriptive names that reflect your project purpose for better organization'
    },
    projectType: {
      title: 'Environment Selection - Deployment Type',
      content: 'Choose your development environment. Local projects run on your machine with full control, while cloud projects offer scalable hosting.',
      label: 'BEST FOR',
      labelText: 'Local development, cloud hosting, environment configuration',
      tip: 'Local gives you complete control, cloud provides instant scalability and collaboration'
    },

    actionButtons: {
      title: 'Project Actions - Creation Controls',
      content: 'Control your project creation process with options to go back, cancel, or proceed with your configured settings.',
      label: 'BEST FOR',
      labelText: 'Project navigation, creation control, configuration management',
      tip: 'Review your selections before creating - you can always modify the project structure later'
    },
    // Empty Project Template
    emptyTemplate: {
      title: 'Empty Project Template',
      content: 'Complete creative freedom with no predefined structure. Perfect for experienced creators who want full control over their project setup.',
      label: 'BEST FOR',
      labelText: 'Advanced users comfortable with custom projects',
      additionalInfo: 'You\'ll Get: Clean workspace, full customization, unlimited possibilities',
      tip: 'Choose this if you have a specific vision and prefer building from the ground up. Prompt the Kolega Studio to help you design the architecture and requirements before you start building.'
    },
    
    // Marketing Website Template
    marketingTemplate: {
      title: 'Professional Website',
      content: 'Launch-ready website template with modern design, SEO optimization, and mobile responsiveness. Perfect for businesses, products, or personal brands.',
      label: 'BEST FOR',
      labelText: 'Beginners to intermediate users wanting quick results',
      additionalInfo: 'You\'ll Get: Pre-built pages, responsive design, SEO-friendly structure',
      tip: 'Ideal for getting online quickly with professional results'
    },
    
    // Full Stack Web App Template
    fullStackTemplate: {
      title: 'Complete Web Application',
      content: 'Full-featured application with user login system, data storage, and modern interface components. Built with professional web technologies.',
      label: 'BEST FOR',
      labelText: 'Intermediate to advanced users building complex applications',
      additionalInfo: 'You\'ll Get: User login system, data storage, modern interface, scalable structure',
      tip: 'Perfect for e-commerce, marketplaces, or SaaS platforms'
    },
    
    createButton: {
      title: 'Create Project - Initialize Development',
      content: 'Initialize your new project with the selected template and configuration. Kolega will set up the complete project environment.',
      label: 'BEST FOR',
      labelText: 'Project initialization, template setup, project environment configuration',
      tip: 'Review your selections before creating - you can always modify the project structure later'
    },
    
    // Individual Template Tooltips - Database Templates
    
    // Business Website Template
    'business-website-template': {
      title: 'Business Website Template',
      content: 'Looking to establish your business online? This template provides a solid foundation to build a professional website for your company.',
      label: 'BEST FOR',
      labelText: 'Professional business presence, company showcasing, client engagement',
      tip: 'Perfect for establishing credibility and attracting customers online'
    },
    
    // E-commerce Store Template
    'e-commerce-store-template': {
      title: 'E-commerce Store Template',
      content: 'Want to sell products online? This template gives you a starting point to build your e-commerce platform with essential store features.',
      label: 'BEST FOR',
      labelText: 'Online retail, product sales, digital commerce',
      tip: 'Everything you need to start selling products online immediately'
    },
    
    // Web Game Template
    'web-game-template': {
      title: 'Web Game Template',
      content: 'Interested in creating browser-based games? This template provides the foundation to build interactive web games with basic game mechanics.',
      label: 'BEST FOR',
      labelText: 'Interactive entertainment, browser games, user engagement',
      tip: 'Create engaging games that run directly in web browsers'
    },
    
    // Admin Dashboard Template
    'admin-dashboard-template': {
      title: 'Admin Dashboard Template',
      content: 'Need to manage business data and operations? This template offers a starting point to build admin dashboards with data visualization capabilities.',
      label: 'BEST FOR',
      labelText: 'Business management, data visualization, admin panels',
      tip: 'Monitor metrics and manage operations with professional dashboards'
    },
    
    // Full Stack Web App Template
    'full-stack-web-app-template': {
      title: 'Full Stack Web App Template',
      content: 'Planning to build a complex web application? This full-stack template provides the foundation for apps requiring user authentication and database integration.',
      label: 'BEST FOR',
      labelText: 'Complex applications, user management, data-driven projects',
      tip: 'Ideal for SaaS platforms, web applications, and enterprise solutions'
    },
    
    // Portfolio Site Template
    'portfolio-site-template': {
      title: 'Portfolio Site Template',
      content: 'Want to showcase your work professionally? This template helps you create a portfolio website to display your projects and skills.',
      label: 'BEST FOR',
      labelText: 'Creative professionals, freelancers, skill demonstration',
      tip: 'Perfect for designers, developers, and creative professionals'
    },
    
    // Blog Platform Template
    'blog-platform-template': {
      title: 'Blog Platform Template',
      content: 'Looking to share your thoughts and build an audience? This template provides the foundation to create a blog platform for content publishing.',
      label: 'BEST FOR',
      labelText: 'Content creators, writers, thought leadership',
      tip: 'Share your expertise and build an audience through quality content'
    },
    
    // Empty Project Template
    'empty-project-template': {
      title: 'Empty Project Template',
      content: 'Have a unique project in mind? Start with a clean slate and build exactly what you envision without any predefined structure.',
      label: 'BEST FOR',
      labelText: 'Advanced users, custom projects, complete control',
      tip: 'Choose this if you have a specific vision and prefer building from the ground up'
    }
  },

  // Project Home Page
  projectHome: {
    threadsPanel: {
      title: 'Conversation History',
      content: 'All your project conversations organized by context and timeline.',
      label: 'ORGANIZATION',
      labelText: 'Grouped by date, searchable, with full context retention',
      tip: 'Use search to quickly find specific agents actions or discussions'
    },
    threadAgentName: {
      title: '"Agent Name" Button',
      content: 'Shows which agent was used to create and handle this conversation thread.',
      label: 'AGENT TYPES',
      labelText: 'Code Agent for development work',
      tip: 'Each thread maintains the agent type it was started with for consistency and conversation context'
    },
    welcomeMessage: {
      title: 'Project Workspace - Getting Started',
      content: 'Your central hub for project work. Start conversations with AI agents, manage files, and coordinate your project workflow.',
      label: 'BEST FOR',
      labelText: 'Project coordination, agent communication, file management',
      tip: 'Begin by selecting an agent and describing what you want to build'
    },
    messageInput: {
      title: 'Natural Language Interface',
      content: 'Communicate with Kolega using natural language. Ask questions, request features, or describe your vision.',
      label: 'EXAMPLES',
      labelText: '"Add a contact form", "Make it mobile-friendly", "Explain this code", "Run some tests", "Start my application"',
      tip: 'Be specific about your goals for best results'
    },
    codeAgent: {
      title: 'Code Agent - Universal Development Expert',
      content: 'Comprehensive agent capable of requirements planning, application design, building, problem-solving, and using advanced tools like browser automation.',
      label: 'CAPABILITIES',
      labelText: 'Planning, design, building, troubleshooting, tool usage, investigation, problem-solving',
      tip: 'Choose this for end-to-end project work and complex challenges'
    },
    sendButton: {
      title: 'Execute Request',
      content: 'Send your message to Kolega for processing and implementation.',
      label: 'SHORTCUT',
      labelText: 'Press Enter to send, Shift+Enter for new line',
      tip: 'Review your request before sending for optimal results'
    },
    projectFiles: {
      title: 'File Management - Project Assets',
      content: 'Upload and manage your project files, images, documents, and code assets. Files uploaded here will be visible to all threads in the project. Drag and drop files or click to browse.',
      label: 'SUPPORTED',
      labelText: 'Images and screenshots',
      tip: 'Files uploaded here are shared across all project threads for easy access and reference'
    },
    threadItem: {
      title: 'Thread Item - Conversation History',
      content: 'Individual conversation threads with Kolega agents. Each thread maintains its own context, agent type, and conversation history. Click to open and continue the conversation.',
      label: 'FEATURES',
      labelText: 'Agent type indicator, running status, conversation context',
      tip: 'Green dot indicates active agent work, gray dot shows idle threads'
    }
  },

  // Thread Interface Page
  threadInterface: {
    // New Thread Button
    newThreadButton: {
      title: 'Start New Conversation',
      content: 'Begin a fresh conversation thread while keeping your current project context.',
      label: 'PERFECT FOR',
      labelText: 'New features, different approaches, or clean brainstorming',
      tip: 'Each thread maintains its own conversation history and context'
    },
    
    // Send Button
    sendButton: {
      title: 'Execute Request',
      content: 'Send your message to Kolega for processing and implementation.',
      label: 'SHORTCUT',
      labelText: 'Press Enter to send, Shift+Enter for new line',
      tip: 'Review your request before sending for optimal results'
    },
    
    // File Upload Area
    fileUpload: {
      title: 'File Upload Area',
      content: 'Upload images, documents, or code files to enhance your project or provide context to Kolega. Files uploaded here will be visible to the current thread only.',
      label: 'SUPPORTED',
      labelText: 'Images and screenshots',
      tip: 'Files uploaded here are thread-specific - drag and drop files directly into this area'
    },

    // Chat Messages Area
    chatMessages: {
      title: 'Chat Messages - Conversation History',
      content: 'View your complete conversation history with Kolega. Messages include your requests, agent responses, tool usage, and system updates.',
      label: 'FEATURES',
      labelText: 'Message search, copy functionality, tool expansion, conversation context',
      tip: 'Use the search function to quickly find specific messages or discussions'
    },

    // Message Input Area
    messageInput: {
      title: 'Message Input - Chat with Kolega',
      content: 'Type your messages, questions, or requests here. Kolega understands natural language and can help with coding, planning, and problem-solving.',
      label: 'EXAMPLES',
      labelText: '"Add a contact form", "Fix this bug", "Explain this code", "Create a new feature"',
      tip: 'Use Shift+Enter for new lines, Enter to send. Be specific for best results'
    },

    // Search Button
    searchButton: {
      title: 'Search Messages - Find in Conversation',
      content: 'Search through your conversation history to quickly find specific messages, discussions, or information. Use keywords to locate past exchanges.',
      label: 'SEARCH TIPS',
      labelText: 'Use keywords, phrases, or specific terms from your conversation',
      tip: 'Navigate results with arrow keys or click through matches to find what you need'
    },

    // Tools Panel Tabs
    monitorTab: {
      title: 'UI Monitor - Live Preview',
      content: 'Watch your application interface and visual components as they are built and updated by Kolega.',
      label: 'PREVIEW',
      labelText: 'UI components, visual feedback, interface monitoring',
      tip: 'See your application come to life as Kolega makes changes and improvements'
    },
    terminalTab: {
      title: 'Terminal - System Commands',
      content: 'View system commands and processes that Kolega runs to build and manage your project.',
      label: 'SYSTEM ACTIVITY',
      labelText: 'Command history, system processes, build operations',
      tip: 'See what Kolega is doing behind the scenes to build your project'
    },
    logsTab: {
      title: 'System Logs - Activity Information',
      content: 'View detailed system messages, status updates, and information about your project build process.',
      label: 'SYSTEM STATUS',
      labelText: 'Status messages, system activity, build information',
      tip: 'Monitor system activity and see detailed information about project operations'
    }
  },

  // Code Editor Page
  codeEditor: {
    downloadRepository: {
      title: 'Download Repository - Export Project',
      content: 'Download your complete project repository as a ZIP file for backup, sharing, or local development.',
      label: 'EXPORT OPTIONS',
      labelText: 'ZIP download, project backup, local development, sharing',
      tip: 'Perfect for creating backups or working offline on your project'
    },
    
    expandCommit: {
      title: 'View Commit Details - File Changes',
      content: 'Click to expand and view the detailed file changes, additions, and deletions for this commit.',
      label: 'COMMIT DETAILS',
      labelText: 'File changes, code diff, commit analysis, change tracking',
      tip: 'See exactly what files were modified in each commit'
    },
    
    resetToCommit: {
      title: 'Reset to Commit - Revert Changes',
      content: 'Reset your repository to this specific commit, permanently removing all subsequent changes.',
      label: 'DANGER ZONE',
      labelText: 'Permanent reset, change removal, commit reversion, data loss',
      tip: 'This action cannot be undone - use with extreme caution'
    },
    fileExplorer: {
      title: 'Project File Navigator - File Management',
      content: 'Browse your complete project structure with file tree navigation, search capabilities, and quick file access.',
      label: 'BEST FOR',
      labelText: 'File navigation, project structure, code organization, file search',
      tip: 'Use the search function to quickly locate specific files or code snippets'
    },
    
    refreshFiles: {
      title: 'Refresh File Tree',
      content: 'Reload the project file structure to reflect any changes made outside the editor.',
      label: 'UPDATES',
      labelText: 'File system sync, external changes, new files detection',
      tip: 'Use this when files are added, removed, or modified externally'
    },
    
    saveFile: {
      title: 'Save File Changes',
      content: 'Save your current file modifications to disk. Changes are preserved and ready for deployment.',
      label: 'SHORTCUT',
      labelText: 'File persistence, change saving, keyboard shortcut ⌘S',
      tip: 'Files auto-save on focus change, but manual save ensures immediate persistence'
    }
  },

  // Header Component
  header: {
    // Main Navigation Buttons
    chatNavButton: {
      title: 'Chat Interface',
      content: 'Switch to the main chat interface where you can communicate with AI agents and manage conversations.',
      label: 'NAVIGATION',
      labelText: 'Project conversations, agent interactions, thread management',
      tip: 'This is your primary workspace for AI-powered development'
    },
    
    codeNavButton: {
      title: 'Code Editor',
      content: 'View and edit your project files. Browse folders, open multiple files in tabs, save your changes, and download your code.',
      label: 'BEST FOR',
      labelText: 'Viewing files, making edits, saving changes, downloading code',
      tip: 'Simple file editor for viewing, modifying, and downloading your project files'
    },
    
    securityNavButton: {
      title: 'Security Dashboard',
      content: 'Monitor security vulnerabilities, code analysis, and compliance status for your project.',
      label: 'SECURITY',
      labelText: 'Vulnerability scanning, code analysis, compliance checks',
      tip: 'Available in localhost development environment'
    },
    
    // Thread Navigation Buttons
    projectToThreadButton: {
      title: 'Go to Last Thread',
      content: 'Navigate to your most recently visited thread in this project. If no thread was visited yet, takes you to the main project workspace.',
      label: 'QUICK ACCESS',
      labelText: 'Resume conversations, return to active discussions, thread history',
      tip: 'Quickly jump back into your ongoing conversations and development discussions'
    },
    
    backToProjectButton: {
      title: 'Project Navigation',
      content: 'Navigate within your current project between the main workspace and your active threads. Click to switch between file management and conversation views.',
      label: 'NAVIGATION FUNCTION',
      labelText: 'From workspace to last active thread, or from thread back to workspace',
      tip: 'Use this to access file uploads or return to your ongoing conversations'
    },
    
    threadChatTab: {
      title: 'Conversation Interface',
      content: 'Your primary workspace for natural language interaction with Kolega Studio.',
      label: 'BEST FOR',
      labelText: 'Planning, brainstorming, and iterative development',
      tip: 'Speak naturally - Kolega understands context and nuance'
    },
    
    threadCodeTab: {
      title: 'Code Editor',
      content: 'Direct access to your project files and code editing',
      label: 'WHEN TO USE',
      labelText: 'Fine-tuning code, reviewing implementations, manual edits',
      tip: 'Switch seamlessly between Chat and Code Editor for optimal workflow'
    },
    
    // User Interface Controls
    userMenuButton: {
      title: 'Account & Settings',
      content: 'Access your profile, project settings, manage your projects and account management options.',
      label: 'QUICK ACCESS',
      labelText: 'Project settings, account preferences, administration, payment and plan options',
      tip: 'Project Settings contains deployment, environments and infrastructure options'
    },
    
    darkModeToggle: {
      title: 'Theme Toggle',
      content: 'Switch between light and dark themes to customize your visual experience.',
      label: 'APPEARANCE',
      labelText: 'Light mode, dark mode, visual preferences',
      tip: 'Theme preference is saved automatically across sessions'
    },
    
    sandboxStatus: {
      title: 'Workspace Status',
      content: 'Shows the current status of your development workspace environment. Click to retry if there are errors.',
      label: 'DEVELOPMENT',
      labelText: 'Environment status, workspace health, error recovery',
      tip: 'Green means ready, yellow means loading, red means error - click red to retry'
    },
    
    helpButton: {
      title: 'Help & Support',
      content: 'Access documentation, tutorials, and support resources for Kolega Studio.',
      label: 'QUICK ACCESS',
      labelText: 'Keyboard shortcuts, feature guides, contact support',
      tip: 'Press "?" for keyboard shortcuts anytime'
    },
    
    // Bug Report System
    bugReportButton: {
      title: 'Bug Report',
      content: 'Report bugs, UI issues, or feature requests directly to the development team. Help improve Kolega Studio by sharing your feedback.',
      label: 'FEEDBACK',
      labelText: 'Bug reporting, issue tracking, feature requests, user feedback',
      tip: 'Include screenshots and detailed descriptions for faster resolution'
    },
    
    bugReportIssueType: {
      title: 'Issue Type Selection',
      content: 'Select the type of issue you\'re experiencing. Choose from bug/error, UI/design issues, feature requests, or performance problems.',
      label: 'CATEGORIZATION',
      labelText: 'Bug classification, issue types, problem categorization',
      tip: 'Accurate categorization helps prioritize and route your report to the right team'
    },
    
    bugReportDescription: {
      title: 'Issue Description',
      content: 'Provide a detailed description of the issue. Include steps to reproduce, expected behavior, and actual behavior for bugs.',
      label: 'DETAILS',
      labelText: 'Problem description, reproduction steps, expected vs actual behavior',
      tip: 'The more detail you provide, the faster we can identify and fix the issue'
    },
    
    bugReportScreenshot: {
      title: 'Screenshot Upload',
      content: 'Upload a screenshot to help illustrate the issue. Visual evidence greatly helps in understanding and reproducing problems.',
      label: 'VISUAL EVIDENCE',
      labelText: 'Screenshot upload, visual documentation, error illustrations',
      tip: 'Screenshots are especially helpful for UI issues and error messages'
    },
    
    bugReportSubmit: {
      title: 'Submit Report',
      content: 'Submit your bug report to the development team. You\'ll receive confirmation and the report will be tracked for resolution.',
      label: 'SUBMISSION',
      labelText: 'Report submission, feedback tracking, issue resolution',
      tip: 'Reports are automatically enriched with system information to help with debugging'
    }
  },

  // Project Settings Page
  projectSettings: {
    backToProject: {
      title: 'Return to Project - Navigation',
      content: 'Navigate back to your project workspace to continue development and access all project features.',
      label: 'NAVIGATION METHOD',
      labelText: 'Quick return to project dashboard, workspace access',
      tip: 'Use this to quickly return to your main project workspace'
    },
    settingsGeneral: {
      title: 'General Settings - Project Configuration',
      content: 'Configure basic project settings including name, description, and general preferences.',
      label: 'BEST FOR',
      labelText: 'Project metadata, basic configuration, general preferences',
      tip: 'Start here for fundamental project setup and information'
    },
    settingsPublish: {
      title: 'Publish Settings - Deployment Management',
      content: 'Manage project deployment, infrastructure setup, and publishing configurations.',
      label: 'BEST FOR',
      labelText: 'Infrastructure management, deployment configuration, publishing setup',
      tip: 'Configure how your project gets deployed and published'
    },
    settingsDelete: {
      title: 'Delete Project - Permanent Removal',
      content: 'Permanently remove your project with a 30-day recovery period. Handle with care.',
      label: 'DANGER ZONE',
      labelText: 'Project deletion, permanent removal, recovery options',
      tip: 'Projects can be restored within 30 days after deletion'
    },
    refreshInfrastructure: {
      title: 'Refresh Infrastructure - Status Update',
      content: 'Update the current status of your infrastructure components and deployment readiness.',
      label: 'BEST FOR',
      labelText: 'Infrastructure monitoring, status updates, deployment readiness',
      tip: 'Use this to check if your infrastructure is ready for deployment'
    },
    refreshDeployments: {
      title: 'Refresh Deployments - Update Status',
      content: 'Get the latest status of your deployments and deployment history.',
      label: 'BEST FOR',
      labelText: 'Deployment monitoring, status tracking, deployment history',
      tip: 'Check deployment progress and view recent deployment activity'
    },
    deployProject: {
      title: 'Deploy Project - Go Live',
      content: 'Deploy your project to production infrastructure. Requires all infrastructure to be ready.',
      label: 'REQUIREMENTS',
      labelText: 'Ready infrastructure, committed changes, deployment configuration',
      tip: 'Ensure all infrastructure shows "Ready" status before deploying'
    },
    deleteProjectButton: {
      title: 'Delete Project - Permanent Action',
      content: 'Permanently delete this project. You will have 30 days to restore it before permanent removal.',
      label: 'DANGER ZONE',
      labelText: 'Permanent deletion, 30-day recovery period, irreversible action',
      tip: 'This action cannot be undone immediately - use with extreme caution'
    }
  },

  // Account Settings
  account: {
    // Navigation sections
    accountProfile: {
      title: 'Profile Settings - Personal Information',
      content: 'Manage your personal information, display name, email address, and basic account preferences.',
      label: 'MANAGE',
      labelText: 'Personal details, display name, email, profile picture',
      tip: 'Start here to update your basic account information'
    },
    accountPassword: {
      title: 'Password Settings - Security Management',
      content: 'Change your account password and manage password security settings.',
      label: 'SECURITY',
      labelText: 'Password changes, security updates, account protection',
      tip: 'Use a strong, unique password for better security'
    },
    accountPlan: {
      title: 'Plan Settings - Subscription Management',
      content: 'View and manage your subscription plan, upgrade or downgrade options, and plan features.',
      label: 'SUBSCRIPTION',
      labelText: 'Plan upgrades, feature access, subscription management',
      tip: 'Compare plans to find the best fit for your needs'
    },
    accountCredits: {
      title: 'Credits Management - Usage Tracking',
      content: 'Monitor your credit usage, view remaining credits, and manage credit-based features.',
      label: 'USAGE TRACKING',
      labelText: 'Credit balance, usage history, feature consumption',
      tip: 'Keep track of your usage to avoid service interruptions'
    },
    accountBilling: {
      title: 'Billing Settings - Payment Management',
      content: 'Manage payment methods, view billing history, download invoices, and update billing information.',
      label: 'PAYMENTS',
      labelText: 'Payment methods, billing history, invoices, payment updates',
      tip: 'Keep your payment information current to avoid service disruptions'
    },
    accountNotifications: {
      title: 'Notification Settings - Communication Preferences',
      content: 'Configure email notifications, alerts, and communication preferences for your account.',
      label: 'COMMUNICATION',
      labelText: 'Email alerts, notification preferences, communication settings',
      tip: 'Customize notifications to stay informed without being overwhelmed'
    },
    accountSecurity: {
      title: 'Security Settings - Account Protection',
      content: 'Manage two-factor authentication, view login history, and configure advanced security settings.',
      label: 'PROTECTION',
      labelText: 'Two-factor auth, login history, security monitoring',
      tip: 'Enable 2FA for enhanced account security'
    },
    
    // Action buttons
    editProfileButton: {
      title: 'Edit Profile - Personal Information',
      content: 'Modify your personal information, display name, and account preferences.',
      label: 'BEST FOR',
      labelText: 'Name changes, email updates, profile customization',
      tip: 'Email changes require verification before taking effect'
    },
    saveProfileButton: {
      title: 'Save Profile - Confirm Changes',
      content: 'Save your profile changes and update your account information.',
      label: 'ACTION REQUIRED',
      labelText: 'Profile updates, information confirmation, account changes',
      tip: 'Email changes will require verification via confirmation code'
    },
    verifyEmailButton: {
      title: 'Verify Email - Confirm Address',
      content: 'Verify your new email address using the confirmation code sent to your inbox.',
      label: 'SECURITY STEP',
      labelText: 'Email verification, account security, address confirmation',
      tip: 'Check your email inbox and spam folder for the verification code'
    },
    changePasswordButton: {
      title: 'Change Password - Security Update',
      content: 'Update your account password with a new secure password meeting all requirements.',
      label: 'SECURITY REQUIREMENTS',
      labelText: '8+ characters, uppercase, lowercase, numbers, special characters',
      tip: 'Use a strong, unique password that you do not use elsewhere'
    },
    saveNotificationsButton: {
      title: 'Save Notifications - Preference Update',
      content: 'Save your notification preferences for email, push, and in-app notifications.',
      label: 'NOTIFICATION TYPES',
      labelText: 'Email alerts, push notifications, in-app messages, preferences',
      tip: 'Customize which notifications you receive and how you receive them'
    },
    toggleMfaButton: {
      title: 'Multi-Factor Authentication - Security Toggle',
      content: 'Enable or disable two-factor authentication for enhanced account security. When enabled, you will need both your password and a verification code to sign in.',
      label: 'SECURITY ENHANCEMENT',
      labelText: 'Account protection, login security, authentication verification',
      tip: 'Enabling MFA significantly increases your account security - highly recommended'
    },
    cancelMfaSetup: {
      title: 'Cancel MFA Setup - Exit Configuration',
      content: 'Cancel the multi-factor authentication setup process and return to security settings.',
      label: 'SETUP CONTROL',
      labelText: 'Setup cancellation, configuration exit, security settings return',
      tip: 'You can restart MFA setup anytime from the security settings'
    },
    verifyMfaCode: {
      title: 'Verify MFA Code - Complete Setup',
      content: 'Enter the 6-digit verification code from your authenticator app to complete MFA setup.',
      label: 'VERIFICATION STEP',
      labelText: 'Code verification, authenticator app, setup completion',
      tip: 'Use Google Authenticator, Authy, or similar apps to generate the code'
    },
    showMoreActivity: {
      title: 'Show Activity History - Authentication Log',
      content: 'View complete authentication activity history including login attempts, locations, and security events.',
      label: 'SECURITY MONITORING',
      labelText: 'Login history, security events, account activity, threat detection',
      tip: 'Review regularly to detect any unauthorized access attempts'
    },
    refreshCredits: {
      title: 'Refresh Credits - Update Balance',
      content: 'Refresh your credit balance and usage statistics to see the most current information.',
      label: 'ACCOUNT SYNC',
      labelText: 'Balance updates, usage refresh, credit synchronization',
      tip: 'Use this if your balance seems outdated or after making a purchase'
    },
    selectPlanButton: {
      title: 'Select Plan - Subscription Management',
      content: 'Choose or change your subscription plan to access different features and credit allowances.',
      label: 'PLAN FEATURES',
      labelText: 'Subscription tiers, feature access, billing management',
      tip: 'Compare features carefully to choose the plan that best fits your needs'
    },
    contactSalesButton: {
      title: 'Contact Sales - Enterprise Support',
      content: 'Get in touch with our sales team for enterprise plans, custom solutions, and volume pricing.',
      label: 'ENTERPRISE OPTIONS',
      labelText: 'Custom pricing, volume discounts, enterprise features, dedicated support',
      tip: 'Perfect for teams and organizations with specific requirements'
    },
    addPaymentMethod: {
      title: 'Add Payment Method - Billing Setup',
      content: 'Add a new credit card or payment method to your account for subscriptions and credit purchases.',
      label: 'PAYMENT OPTIONS',
      labelText: 'Credit cards, billing setup, payment security',
      tip: 'Your payment information is securely encrypted and stored'
    },
    editPaymentMethod: {
      title: 'Edit Payment Method - Update Details',
      content: 'Update your existing payment method details, including card information and billing address.',
      label: 'PAYMENT UPDATES',
      labelText: 'Card updates, billing address, payment modifications',
      tip: 'Keep your payment information current to avoid service interruptions'
    },
    removePaymentMethod: {
      title: 'Remove Payment Method - Delete Card',
      content: 'Remove this payment method from your account. Ensure you have another method if you have active subscriptions.',
      label: 'PAYMENT REMOVAL',
      labelText: 'Card deletion, payment cleanup, billing management',
      tip: 'You need at least one payment method for active subscriptions'
    },
    updateBillingInfo: {
      title: 'Update Billing Info - Account Details',
      content: 'Update your billing information including company name, address, and tax details.',
      label: 'BILLING DETAILS',
      labelText: 'Company info, billing address, tax information, invoice details',
      tip: 'Accurate billing information ensures proper invoicing and tax compliance'
    },
    downloadInvoice: {
      title: 'Download Invoice - Billing Records',
      content: 'Download a PDF copy of this invoice for your records, accounting, or expense reporting.',
      label: 'INVOICE MANAGEMENT',
      labelText: 'PDF download, billing records, expense tracking, accounting',
      tip: 'Keep invoices for tax purposes and expense reimbursement'
    }
  }
};
